import type { OnChangeValue } from '@ksyos/design-system';
import { ButtonRow, Form, FormGroup, Select, TextInput } from '@ksyos/design-system';
import { useState } from 'react';
import type { IOption } from '@frontend/components/interfaces/option';
import type { ValidationErrors } from '@frontend/utils/validation';
import { FormContextProvider } from '@frontend/components/elements/formContext';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { ICustomControlSupervisionEditProps } from '../../interfaces';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { SupervisorsAssessmentBiometrics } from '../../../../../../common/enums/supervisorsAssessmentBiometrics';
import { ToastType } from '../../../../../../common/enums/toastType';
import { translateKey } from '../../../../../translate';
import { showServiceToast } from '../../../../../utils/showToast';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';
import { SaveButton } from '../../../../elements/carePathButtons/SaveButton';
import { SaveSendButton } from '../../../../elements/carePathButtons/SaveSendButton';
import { HeaderDivider } from '../../../../elements/common';
import { TcohV2DiagnosticReportAddEditCustomControlFields } from './TcohV2DiagnosticReportAddEditCustomControlFields';

export function TcohV2SupervisionEdit(props: ICustomControlSupervisionEditProps) {
    const [buttonDisabled, setButtonDisabled] = useState(false);

    return (
        <Form submitOnEnter={false}>
            <HeaderDivider text={translateKey('supervision.qualityProcedure')} />

            <FormGroup
                hasErrors={props.errors.has('assessmentBiometrics')}
                isRequired={true}
                label={translateKey('supervision.qualityCheck')}
            >
                <Select
                    error={getErrorMessage(props.errors, 'assessmentBiometrics')}
                    onBlur={async () => await props.validateOne('assessmentBiometrics')}
                    onChange={async (value: OnChangeValue<IOption<string>, false>) =>
                        await props.handleChange(
                            'assessmentBiometrics',
                            (value?.value as SupervisorsAssessmentBiometrics) || null
                        )
                    }
                    options={getSupervisorsAssessmentBiometricsOptions()}
                    placeholder={translateKey('common.describeOrChooseFromList')}
                    value={getSupervisorsAssessmentBiometricsOptions().find(
                        (option) => option.value === props.supervision.assessmentBiometrics
                    )}
                />
            </FormGroup>

            <FormGroup
                hasErrors={props.errors.has('remarkBiometrics')}
                label={translateKey('supervision.remarkBiometrics')}
            >
                <TextInput
                    error={getErrorMessage(props.errors, 'remarkBiometrics')}
                    onBlur={async () => await props.validateOne('remarkBiometrics')}
                    onChange={async (e) => await props.handleChange('remarkBiometrics', e.target.value || null)}
                    placeholder={translateKey('common.remarksOrQuestions')}
                    value={props.supervision.remarkBiometrics || ''}
                    variant="outline"
                />
            </FormGroup>

            <HeaderDivider text={translateKey('supervision.qualityReport')} />

            <FormContextProvider
                value={{
                    placeholderGroupName: 'supervision',
                    item: props.supervision.customControlData,
                    errors: props.errors,
                    customControlData: true,
                    onChange: async (key, value) => await props.handleChangeCustomControl({ [key]: value }),
                    onDoValidate: async (key) => await props.validateOneCustomControl(key),
                }}
            >
                <TcohV2DiagnosticReportAddEditCustomControlFields
                    customControlData={props.supervision.customControlData}
                    errors={props.errors}
                    handleChangeCustomControl={props.handleChangeCustomControl}
                    isAdviesBijAfwijkendeBeoordelingRequested={false}
                    service={props.service}
                    validateOneCustomControl={props.validateOneCustomControl}
                />
            </FormContextProvider>

            <FormGroup
                hasErrors={props.errors.has('remarkGrading')}
                label={translateKey('supervision.remarkGrading')}
            >
                <TextInput
                    error={getErrorMessage(props.errors, 'remarkGrading')}
                    onBlur={async () => await props.validateOne('remarkGrading')}
                    onChange={async (e) => await props.handleChange('remarkGrading', e.target.value || null)}
                    placeholder={translateKey('common.remarksOrQuestions')}
                    value={props.supervision.remarkGrading || ''}
                    variant="outline"
                />
            </FormGroup>

            <ButtonRow justify="right">
                <CancelButton
                    cancel={props.onCancel}
                    entity={ProcessEntity.supervision}
                    serviceCode={props.service.code}
                    serviceType={props.service.serviceType}
                />
                <SaveButton
                    buttonDisabled={buttonDisabled}
                    save={async () => {
                        await saveSupervision();
                    }}
                />
                <SaveSendButton
                    buttonDisabled={buttonDisabled}
                    saveSend={async () => {
                        await saveSendSupervision();
                    }}
                />
            </ButtonRow>
        </Form>
    );

    function getErrorMessage(errors: ValidationErrors, key: string) {
        return getValidationErrorTranslation(errors.get(key));
    }

    function getSupervisorsAssessmentBiometricsOptions() {
        return Object.values(SupervisorsAssessmentBiometrics).map((value) => {
            return { value, label: translateKey(`supervisorsAssessmentBiometricsEnum.${value}`) };
        });
    }

    async function saveSendSupervision() {
        setButtonDisabled(true);

        const validatedSupervision = await props.validateSupervision('final');
        if (!validatedSupervision) {
            setButtonDisabled(false);

            return;
        }

        const supervisionId = await props.onSaveSupervision('final');
        setButtonDisabled(false);

        showServiceToast(
            ToastType.success,
            'toast.submitSuccess',
            props.service,
            'supervision',
            translateKey('toast.submitDefaultMessage')
        );
        await props.onSave(supervisionId);
    }

    async function saveSupervision() {
        setButtonDisabled(true);

        const validatedSupervision = await props.validateSupervision('draft');
        if (!validatedSupervision) {
            setButtonDisabled(false);

            return;
        }

        const supervisionId = await props.onSaveSupervision('draft');
        setButtonDisabled(false);

        showServiceToast(
            ToastType.success,
            'toast.draftSuccess',
            props.service,
            'supervision',
            translateKey('toast.draftDefaultMessage')
        );
        await props.onSave(supervisionId);
    }
}
